import React from "react"
import DocumentDetails from "./DocumentDetails"
import gql from "graphql-tag"
import { graphql } from "@apollo/client/react/hoc"
import { Row, Col, Card, Button, UncontrolledTooltip, Alert } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import 'react-loading-skeleton/dist/skeleton.css'
import { cleanDocumentUrl } from "../../Material/DocumentModal/DocumentsListNew"
import { isImage, isPdf, isVideo } from "../../Material/DocumentModal/utils"
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import withApolloClient from "../../withApolloClient"
import UploadIncidentDocumentModal from "../Components/UploadIncidentDocumentModal"
import DocumentModalNew from "../../Material/DocumentModal/DocumentModalNew"
import IncidentDocumentLink from "../Components/IncidentDocumentLink"
import MoveIncidentDocumentModal from "../IncidentModals/MoveIncidentDocumentModal"
import { HasPermissionsQuery } from "../../Functions/querys"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import "./DocumentsTab.css"

const GENERATE_PDF = gql`mutation generatePdfFromImages($input: GeneratePDFFromImagesMutationInput!) {
  generatePdfFromImages(input: $input) {
    ok
    base64String
    errors { 
        messages
    }
  }
}`

const AllIncidentDocumentsQuery = gql`query AllIncidentDocuments($incidentId: ID!, $slug: String, $isArchived: Boolean, $first: Int, $after: String) {
    allIncidentDocuments(incidentId: $incidentId, slug: $slug, isArchived: $isArchived, first: $first, after: $after, orderBy: ["-date_modified"]) {
      edges {
        node {
          id
          pk
          dateAdded
          isArchived
          documentType{ 
            id 
            typeName: name
          }
          url
          document
          notes
          createdBy { 
            id 
            username
          }
          history { 
            date 
            user 
            comment
          }
        }
      }
      pageInfo{ 
        endCursor 
        hasNextPage 
      }
    }
}`

const AllCarPicturesQuery = gql`
  query AllCarPictures($incidentId: ID!) {
    allCarPictures(incidentId: $incidentId) {
      edges {
        node {
          id
          dateAdded
          pictureUrl
          pictureType
          description
        }
      }
    }
  }
`
const IncidentBranchStateQuery = gql`
  query IncidentBranchStateQuery($incidentId: ID!) {
    incident(incidentId: $incidentId) {
      id
      branch {
        id
        stateCode
      }
    }
  }
`

const SendIncidentDarMv104RequestMutation = gql`
  mutation SendIncidentDarMv104RequestMutation($input: SendIncidentDarMv104RequestMutationInput!) {
    sendIncidentDarMv104Request(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

class DocumentList extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            openModal: "",
            documents: [],
            selectedDocument: null,
            selectedDocumentContext: null,
            selectedDocs: [],
            pdfBase64: null,
            error: null,
            successMessage: null,
            loading: false,
            actionMenuOpen: false,
            moveModalOpen: false,
            inspectionOpen: false,
            sendDarMv104Loading: false
        }
        this.actionsRef = React.createRef()
    }

    toggleSelectDoc = (doc) => {
        let selected = [...this.state.selectedDocs]
        const exists = selected.find(d => d.id === doc.id)

        if (exists) selected = selected.filter(d => d.id !== doc.id)
        else selected.push(doc)

        this.setState(prevState => ({
            selectedDocs: selected,
            actionMenuOpen: selected.length > 0 ? prevState.actionMenuOpen : false,
            moveModalOpen: selected.length > 0 ? prevState.moveModalOpen : false
        }))
    }

    getSelectableDocs = () => {
        const documentEdges = this.props.allIncidentDocuments?.edges || []
        const pictureEdges = this.props.viewType !== "party" && this.state.inspectionOpen === true ? this.props.allCarPictures?.edges || [] : []

        const incidentDocs = documentEdges?.map(edge => edge.node) || []
        const pictureDocs = pictureEdges?.map(edge => edge.node)?.map(doc => ({
            id: doc.id,
            pictureUrl: doc.pictureUrl,
            documentType: { typeName: doc.pictureType || "Inspection Photo" }
        })) || []
        return [...incidentDocs, ...pictureDocs]
    }

    toggleSelectAll = () => {
        const selectableDocs = this.getSelectableDocs()
        const selectedDocIds = new Set(this.state.selectedDocs.map(doc => doc.id))
        const nextDocs = [...this.state.selectedDocs]
        selectableDocs.forEach(doc => {
            if (!selectedDocIds.has(doc.id)) {
                nextDocs.push(doc)
            }
        })
        this.setState({ selectedDocs: nextDocs })
    }

    clearSelectedDocs = () => {
        this.setState({ selectedDocs: [], actionMenuOpen: false, moveModalOpen: false })
    }

    base64ToBlob = (base64) => {
        try {
            if (!base64 || typeof base64 !== "string")
                throw new Error("Invalid base64 input")

            const byteCharacters = atob(base64)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)

            return new Blob([byteArray], { type: "application/pdf" })
        } catch (e) {
            console.error("base64ToBlob failed: ", e)
            return null
        }
    }


    pdfHandler = async () => {
        const allImagesSelected = this.state.selectedDocs.length > 0
            && this.state.selectedDocs.every(doc => isImage(cleanDocumentUrl(doc.pictureUrl || doc.url), doc.document))
        if (!allImagesSelected) {
            this.setState({ error: "Select only images to download." })
            return
        }
        this.setState({ loading: true, error: null })
        const docs = this.state.selectedDocs.map(d => ({
            bucketPath: d.document && !d.pictureUrl ? d.document : null,
            imageUrl: d.pictureUrl || null,
            title: d.documentType?.typeName || "Document"
        }))
        try {
            const result = await this.props.apolloClient.mutate({
                mutation: GENERATE_PDF,
                variables: {
                    input: {
                        documents: docs,
                        imagesPerPage: 2
                    }
                }
            })
            if (result.data?.generatePdfFromImages?.ok) {
                const blob = this.base64ToBlob(result.data.generatePdfFromImages.base64String)
                if (!blob) {
                    this.setState({ error: "Failed to decode generated PDF.", loading: false })
                    return
                }
                if (this.state.pdfBase64) {
                    URL.revokeObjectURL(this.state.pdfBase64)
                }
                const pdfUrl = URL.createObjectURL(blob)
                this.setState({ pdfBase64: pdfUrl, loading: false, selectedDocs: [] })
            } else {
                this.setState({ error: "Failed to generate PDF.", loading: false })
            }
        } catch (err) {
            this.setState({ error: "An error occurred: " + err.message, loading: false })
        }
    };


    refetchQuery = () => {
        this.props.refetchDocumentsQuery && this.props.refetchDocumentsQuery()
    }

    viewDocuments = (documents, index = 0, context = {}) => {
        const nextContext = {
            objectType: "incident",
            isPicture: false,
            ...context,
        }
        documents = documents?.map(doc => ({
            documentUrl: cleanDocumentUrl(doc.node.pictureUrl || doc.node.url),
            id: doc.node.id,
            name: doc.node.pictureType || doc.node.document,
            documentType: doc.node.documentType,
            description: doc.node.description || doc.node.notes,
            dateAdded: doc.node.dateAdded,
            isArchived: doc.node.isArchived,
            notes: doc.node.notes,
            history: doc.node.history,
        }))
        this.setState({ documents: documents, selectedDocument: documents[index], selectedDocumentContext: nextContext })
    }
    next = () => {
        const documents = this.state.documents
        const currentIndex = documents.findIndex(item => item.id === this.state.selectedDocument.id)
        if (currentIndex === documents.length - 1) return
        this.setState({ selectedDocument: documents[currentIndex + 1] })
    }

    prev = () => {
        const documents = this.state.documents
        const currentIndex = documents.findIndex(item => item.id === this.state.selectedDocument.id)
        if (currentIndex === 0) return
        this.setState({ selectedDocument: documents[currentIndex - 1] })
    }

    componentDidUpdate(prevProps, prevState) {
        const prevCount = prevState.selectedDocs.length
        const nextCount = this.state.selectedDocs.length

        if (prevCount === 0 && nextCount > 0 && this.actionsRef.current) {
            requestAnimationFrame(() => {
                this.actionsRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
            })
        }

        if (prevCount > 0 && nextCount === 0 && (this.state.actionMenuOpen || this.state.moveModalOpen)) {
            // Close action menu/modals when selection is cleared.
            this.setState({ actionMenuOpen: false, moveModalOpen: false })
        }
    }

    toggleActionMenu = () => {
        this.setState(prevState => ({ actionMenuOpen: !prevState.actionMenuOpen }))
    }
    toggleInspectionSection = () => {
        this.setState(prevState => {
            return {
                inspectionOpen: !prevState.inspectionOpen,
                selectedDocs: !prevState.inspectionOpen ? prevState.selectedDocs : []
            }
        })
    }

    getDarMv104DocumentName = () => {
        if (this.props.incidentBranchLoading) return ""
        return (this.props.incidentBranchStateCode || "").toUpperCase() === "NY" ? "MV104" : "DAR"
    }

    sendDarMv104Request = async () => {
        let documentName = this.getDarMv104DocumentName()
        this.setState({ sendDarMv104Loading: true, error: null, successMessage: null })
        try {
            const result = await this.props.apolloClient.mutate({
                mutation: SendIncidentDarMv104RequestMutation,
                variables: {
                    input: {
                        incidentId: this.props.id
                    }
                }
            })

            const response = result?.data?.sendIncidentDarMv104Request
            if (response?.ok) {
                this.setState({
                    openModal: "",
                    sendDarMv104Loading: false,
                    error: null,
                    successMessage: `${documentName} request sent successfully.`
                })
                return
            }

            const error = response?.errors?.[0]?.messages?.join(", ") || `Failed to send ${documentName} request.`
            this.setState({ openModal: "", sendDarMv104Loading: false, error })
        } catch (err) {
            this.setState({
                openModal: "",
                sendDarMv104Loading: false,
                error: err.message || `Failed to send ${documentName} request.`
            })
        }
    }

    canSendDarMv104Document = () => {
        if (this.props.category !== "INCIDENT" || this.props.incidentBranchLoading) return false
        const isNyBranch = (this.props.incidentBranchStateCode || "").toUpperCase() === "NY"
        return isNyBranch
            ? this.props.hasPermissions?.includes("custom_can_send_mv104")
            : this.props.hasPermissions?.includes("custom_can_send_dar")
    }

    render() {
        const { loading, allIncidentDocuments, allCarPictures, isArchived } = this.props
        const documentEdges = allIncidentDocuments?.edges || []
        const selectableDocs = this.getSelectableDocs()
        const selectedCount = this.state.selectedDocs?.length
        const selectedDocIds = new Set(this.state.selectedDocs.map(doc => doc.id))
        const allSelected = selectableDocs.length > 0 && selectableDocs.every(doc => selectedDocIds.has(doc.id))
        const hasDownloadableSelection = selectedCount > 0 && this.state.selectedDocs.every(doc => isImage(cleanDocumentUrl(doc.pictureUrl || doc.url), doc.document))
        const movableDocs = this.state.selectedDocs.filter(doc => !doc.pictureUrl)
        const canMove = selectedCount > 0 && movableDocs.length === selectedCount
        return (
            <>
                {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                {this.state.successMessage && <Alert color="success">{this.state.successMessage}</Alert>}
                {this.state.pdfBase64 &&
                    <DocumentModalNew
                        document={{
                            documentUrl: this.state.pdfBase64,
                            name: "Generated PDF"
                        }}
                        isPicture={true}
                        objectType="incident"
                        handleClose={() => {
                            URL.revokeObjectURL(this.state.pdfBase64)
                            this.setState({ pdfBase64: null })
                        }}
                    />
                }
                {!isArchived && <div className="text-right mb-4">
                    <Button color="primary" onClick={() => this.setState({ openModal: "uploadIncidentDocument" })} disabled={this.state.loading}>
                        Upload Document
                    </Button>
                    {this.props.hasPermissions?.includes("custom_can_request_documents") && <>
                        &nbsp;&nbsp;
                        <Button color="primary" onClick={() => this.setState({ openModal: "documentLinkModal" })} disabled={this.state.loading}>
                            Request Missing Documents
                        </Button>
                    </>}
                    {this.canSendDarMv104Document() && <>
                        &nbsp;&nbsp;
                        <Button color="primary" onClick={() => this.setState({ openModal: "sendDarMv104Confirm", error: null, successMessage: null })} disabled={this.state.loading || this.state.sendDarMv104Loading}>
                            {`Send ${this.getDarMv104DocumentName()}`}
                        </Button>
                    </>}
                    {this.state.openModal === "sendDarMv104Confirm" && (
                        <ConfirmationPopup
                            open={this.state.openModal === "sendDarMv104Confirm"}
                            handleClose={() => this.setState({ openModal: "", sendDarMv104Loading: false })}
                            loading={this.state.sendDarMv104Loading}
                            action={`Send ${this.getDarMv104DocumentName()}`}
                            color="primary"
                            message={`Are you sure you want to send ${this.getDarMv104DocumentName()} document?`}
                            confirmAction={this.sendDarMv104Request}
                        />
                    )}
                    {this.state.openModal === "documentLinkModal" &&
                        <IncidentDocumentLink
                            incidentId={this.props.id}
                            isOpen={this.state.openModal === "documentLinkModal"}
                            toggle={() => this.setState({ openModal: "" })}
                            category={this.props.category || ""}
                            slug={this.props.slug}
                        />
                    }
                    {this.state.openModal === "uploadIncidentDocument" && (
                        <UploadIncidentDocumentModal
                            incidentId={this.props.id}
                            isOpen={this.state.openModal === "uploadIncidentDocument"}
                            toggle={() => this.setState({ openModal: "" })}
                            refetchQuery={this.props.refetchDocumentsQuery}
                            documentCategory={this.props.category || ""}
                            slug={this.props.slug}
                        />
                    )}
                </div>}
                {loading && <Row>
                    {[...Array(10)].map((_, index) => (
                        <Col className="mt-3" xs={4} key={index}>
                            <Skeleton count={1} height={50} />
                        </Col>
                    ))}
                </Row>}
                {this.state.selectedDocument &&
                    <DocumentModalNew
                        document={this.state.selectedDocument}
                        handleClose={() => this.setState({ selectedDocument: null })}
                        next={this.next}
                        prev={this.prev}
                        objectType={this.state.selectedDocumentContext?.objectType || "incident"}
                        isPicture={this.state.selectedDocumentContext?.isPicture ?? false}
                        refetch={this.props.refetchDocumentsQuery}
                        currentUser={this.props.currentUser}
                        documentCategory={this.props.category || ""}
                    />
                }
                <div className="DocumentCardContainer">
                    {!loading && documentEdges.length > 0 ? (documentEdges.map((document, idx) => (
                        <DocumentDetails
                            key={`incident-doc-${idx}`}
                            document={document.node}
                            viewDocuments={() => this.viewDocuments(documentEdges, idx, { objectType: "incident", isPicture: false })}
                            refetchQuery={this.props.refetchDocumentsQuery}
                            toggleSelectDoc={this.toggleSelectDoc}
                            isLoading={this.state.loading}
                            isSelected={!!this.state.selectedDocs.find(d => d.id === document.node.id)}
                            viewType={this.props.viewType}
                            incidentId={this.props.id}
                        />
                    ))) : !loading &&
                    <p className="danger">No Incident Document Found!</p>
                    }
                    {!loading && allIncidentDocuments?.pageInfo?.hasNextPage && (
                        <div className="w-100 text-right mt-3">
                            <Button
                                color="primary"
                                size="sm"
                                disabled={this.state.loading}
                                onClick={() => { this.props.loadMoreEntries && this.props.loadMoreEntries() }}
                            >
                                Load More
                            </Button>
                        </div>
                    )}
                </div>
                {/* Car Pictures */}
                {this.props.viewType === "incident" && allCarPictures?.edges?.length > 0 && (
                    <>
                        <div className="inspection-section-header">
                            <h5 className="mb-0">Inspection Documents <span className="inspection-section-count">{allCarPictures.edges.length}</span></h5>
                            <i
                                onClick={this.toggleInspectionSection}
                                className={`fa ${this.state.inspectionOpen ? "fa-chevron-up" : "fa-chevron-down"}`}
                                role="button"
                                aria-expanded={this.state.inspectionOpen}
                                aria-label={this.state.inspectionOpen ? "Collapse inspection documents" : "Expand inspection documents"}
                            />
                        </div>
                        {this.state.inspectionOpen && (
                            <div className="DocumentCardContainer">
                                {allCarPictures.edges.map((pic, idx) => (
                                    <Card className="DocumentCard incident-doc-card" key={idx}>
                                        <div className="DocumentCardHeader incident-doc-header">
                                            <p className="incident-doc-title mb-0">
                                                <i className={"fa fa-picture-o"} aria-hidden="true" />&nbsp;{pic.node.pictureType || "Inspection Photo"}
                                            </p>
                                            <div className="incident-doc-actions">
                                                {isImage(cleanDocumentUrl(pic.node.pictureUrl)) &&
                                                    <input
                                                        type="checkbox"
                                                        className="doc-select-checkbox"
                                                        checked={!!this.state.selectedDocs.find(d => d.id === pic.node.id)}
                                                        disabled={this.state.loading}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => this.toggleSelectDoc({
                                                            id: pic.node.id, pictureUrl: pic.node.pictureUrl,
                                                            documentType: { typeName: pic.node.pictureType || "Inspection Photo" }
                                                        })}
                                                    />
                                                }
                                                <i className="fa fa-arrows-alt" onClick={() => !this.state.loading && this.viewDocuments(allCarPictures.edges, idx, { objectType: "picture", isPicture: true })} title="Preview Document" />
                                            </div>
                                        </div>
                                        <div className="DocumentCardPreview">
                                            {isImage(cleanDocumentUrl(pic.node.pictureUrl)) ? (
                                                <img className="DocumentCardPreviewEmbed DocViewerImage" src={cleanDocumentUrl(pic.node.pictureUrl)} alt="Inspection" />
                                            ) : isVideo(cleanDocumentUrl(pic.node.pictureUrl), pic.node.pictureType) ? (
                                                <video className="DocumentCardPreviewEmbed DocViewerVideo" src={cleanDocumentUrl(pic.node.pictureUrl)} controls playsInline preload="metadata" />
                                            ) : isPdf(cleanDocumentUrl(pic.node.pictureUrl), pic.node.pictureType) ? (
                                                <iframe scrolling="no" className="DocumentCardPreviewEmbed" src={cleanDocumentUrl(pic.node.pictureUrl)} title={`inspection-iframe-${idx}`} />
                                            ) : (
                                                <div className="DocumentCardPreviewEmbed d-flex align-items-center justify-content-center">
                                                    <div className="text-center px-2">
                                                        <i className="fa fa-file-text-o fa-2x mb-2" aria-hidden="true" />
                                                        <p>Preview isn’t available here. Click expand icon to view or download the document.</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="incident-doc-footer">
                                            <p className="incident-doc-text mb-1">
                                                Uploaded at <b style={{ color: "#374151" }}>{pic.node.dateAdded ? <DatetimeRenderer datetime={pic.node.dateAdded} /> : "--"}</b>
                                            </p>
                                            <p className="incident-doc-text mb-0">
                                                <b style={{ color: "#374151" }}>Description: </b>{pic.node.description || "---"}&nbsp;&nbsp;
                                            </p>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </>
                )
                }
                {
                    selectableDocs.length > 0 && selectedCount > 0 && (
                        <div className="incident-actions-container" ref={this.actionsRef}>
                            <div className="incident-actions-stack">
                                {this.state.actionMenuOpen && (
                                    <>
                                        <Button className={`floating-btn select-all-btn ${selectedCount > 0 ? "has-selection" : ""}`} id="unselect-btn" color="info" onClick={this.clearSelectedDocs} disabled={this.state.loading}>
                                            <>
                                                <span className="select-count">{selectedCount}</span>
                                                <span className="select-clear" aria-hidden="true">
                                                    <i className="fa fa-times" />
                                                </span>
                                            </>
                                        </Button>
                                        <UncontrolledTooltip target="unselect-btn" placement="top">
                                            Unselect All Documents
                                        </UncontrolledTooltip>
                                        {!allSelected && <>
                                            <Button className="floating-btn" id="select-all-menu-btn" color="info" onClick={this.toggleSelectAll} disabled={this.state.loading}>
                                                <i className="fa fa-check-square-o" />
                                            </Button>
                                            <UncontrolledTooltip target="select-all-menu-btn" placement="top">
                                                Select All Documents
                                            </UncontrolledTooltip>
                                        </>
                                        }

                                        <Button className="floating-btn" id="move-btn" color="warning" onClick={() => this.setState({ moveModalOpen: true })} disabled={!canMove || this.state.loading}>
                                            <i className="fa fa-exchange" />
                                        </Button>
                                        <UncontrolledTooltip target="move-btn" placement="top">
                                            {canMove ? "Move Document" : "Select incident documents only for move feature"}
                                        </UncontrolledTooltip>
                                        <Button className="floating-btn" id="download-btn" color="success" onClick={this.pdfHandler} disabled={this.state.loading || !hasDownloadableSelection}>
                                            {this.state.loading ? <div className="spinner-border spinner-border-sm" /> : <i className="fa fa-download" />}
                                        </Button>
                                        <UncontrolledTooltip target="download-btn" placement="top">
                                            {hasDownloadableSelection ? "Download Selected as PDF" : "Select only images to download"}
                                        </UncontrolledTooltip>
                                    </>
                                )}
                                <Button className="floating-btn" id="actions-btn" style={{ background: "#303e67" }} onClick={this.toggleActionMenu} disabled={this.state.loading}>
                                    <i className="fa fa-ellipsis-v" />
                                </Button>
                                <UncontrolledTooltip target="actions-btn" placement="top">Actions</UncontrolledTooltip>
                            </div>
                        </div>
                    )}
                {canMove && (
                    <MoveIncidentDocumentModal
                        isOpen={this.state.moveModalOpen}
                        onClose={() => this.setState({ moveModalOpen: false })}
                        onMoved={() => this.setState({ selectedDocs: [], moveModalOpen: false, actionMenuOpen: false })}
                        incidentId={this.props.id}
                        documents={movableDocs}
                        viewType={this.props.viewType}
                        refetch={this.props.refetchDocumentsQuery}
                    />
                )
                }
            </>
        )
    }
}

export default withApolloClient(
    graphql(AllCarPicturesQuery, {
        options: ({ id }) => ({
            variables: { incidentId: id },
            fetchPolicy: "network-only"
        }),
        props: ({ data: { allCarPictures, loading } }) => ({
            allCarPictures,
            picturesLoading: loading
        })
    })(
        graphql(AllIncidentDocumentsQuery, {
            options: ({ id, isArchived, slug }) => ({
                variables: { incidentId: id, slug, isArchived, first: 5 },
                fetchPolicy: "network-only",
                notifyOnNetworkStatusChange: true,
            }),
            props({ data: { allIncidentDocuments, loading, refetch, fetchMore, variables } }) {
                return {
                    loading,
                    allIncidentDocuments,
                    variables,
                    refetch,
                    refetchDocumentsQuery: () =>
                        fetchMore({
                            query: AllIncidentDocumentsQuery,
                            variables: { ...variables, first: 5 },
                            updateQuery: (_, { fetchMoreResult }) => {
                                const newEdges = fetchMoreResult?.allIncidentDocuments?.edges || []
                                const pageInfo = fetchMoreResult?.allIncidentDocuments?.pageInfo
                                return {
                                    allIncidentDocuments: {
                                        edges: [...newEdges],
                                        pageInfo,
                                        __typename: "AllIncidentDocuments"
                                    }
                                }
                            }
                        }),
                    loadMoreEntries: () =>
                        fetchMore({
                            query: AllIncidentDocumentsQuery,
                            variables: {
                                after: allIncidentDocuments.pageInfo.endCursor,
                                ...variables
                            },
                            updateQuery: (prev, { fetchMoreResult }) => {
                                const newEdges = fetchMoreResult?.allIncidentDocuments?.edges || []
                                const pageInfo = fetchMoreResult?.allIncidentDocuments?.pageInfo
                                return {
                                    allIncidentDocuments: {
                                        edges: [...prev.allIncidentDocuments.edges, ...newEdges],
                                        pageInfo,
                                        __typename: prev.allIncidentDocuments.__typename
                                    }
                                }
                            }
                        })
                }
            }
        })(
            graphql(IncidentBranchStateQuery, {
                options: ({ id }) => ({
                    variables: { incidentId: id },
                    fetchPolicy: "network-only",
                }),
                props({ data: { incident, loading } }) {
                    return {
                        incidentBranchLoading: loading,
                        incidentBranchStateCode: incident?.branch?.stateCode || null,
                    }
                }
            })(
                graphql(HasPermissionsQuery, {
                    options: {
                        variables: {
                            userPermissions: ["custom_can_request_documents", "custom_can_send_dar", "custom_can_send_mv104"],
                        },
                    },
                    props({ data: { hasPermissions, variables } }) {
                        return {
                            hasPermissions,
                            variables,
                        }
                    },
                })(DocumentList)
            )
        )
    )
)
